import { CloseOutlined } from '@ant-design/icons';
import {
  Alert,
  Card,
  Descriptions,
  Skeleton,
  Space,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { GlobalToken } from 'antd';
import type { TFunction } from 'i18next';
import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchFreightRestriction } from '@/api/endpoints/freightRestrictions';
import type {
  FreightRestrictionKind,
  FreightRestrictionRecord,
} from '@/types/freightRestrictions';

const POPUP_WIDTH_PX = 320;

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'success'; readonly record: FreightRestrictionRecord }
  | { readonly status: 'error'; readonly message: string };

interface FreightRestrictionPopupProps {
  readonly wayId: number;
  readonly onClose: () => void;
}

export default function FreightRestrictionPopup({
  wayId,
  onClose,
}: FreightRestrictionPopupProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    fetchFreightRestriction(wayId, controller.signal)
      .then((record) => {
        if (!controller.signal.aborted) {
          setState({ status: 'success', record });
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'unknown error',
        });
      });
    return () => controller.abort();
  }, [wayId]);

  return (
    <div style={_containerStyle(token)}>
      <Card
        size="small"
        styles={{ body: { padding: 12 } }}
        style={{ background: 'transparent', border: 'none' }}
        title={(
          <Space size="small">
            <Tag color={KIND_TO_TAG_COLOR[_kindOrPlaceholder(state)]}>
              {t(`freightRestrictions.kind.${_kindOrPlaceholder(state)}`)}
            </Tag>
            <Typography.Text strong>
              {t('freightRestrictions.popup.title')}
            </Typography.Text>
          </Space>
        )}
        extra={(
          <CloseOutlined
            onClick={onClose}
            aria-label={t('common.close')}
            style={{ cursor: 'pointer' }}
          />
        )}
      >
        {state.status === 'loading' && <Skeleton active paragraph={{ rows: 2 }} />}
        {state.status === 'error' && (
          <Alert
            type="error"
            message={t('freightRestrictions.popup.errorTitle')}
            description={state.message}
            showIcon
          />
        )}
        {state.status === 'success' && (
          <FreightRestrictionDetails record={state.record} />
        )}
      </Card>
    </div>
  );
}

interface FreightRestrictionDetailsProps {
  readonly record: FreightRestrictionRecord;
}

function FreightRestrictionDetails({ record }: FreightRestrictionDetailsProps) {
  const { t } = useTranslation();
  const items = _detailItems(record, t);
  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {(record.name || record.ref) && (
        <Typography.Text strong>
          {[record.ref, record.name].filter(Boolean).join(' · ')}
        </Typography.Text>
      )}
      <Descriptions
        size="small"
        column={1}
        items={items}
        labelStyle={{ width: 140 }}
      />
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        OSM way #{record.way_id}
      </Typography.Text>
    </Space>
  );
}

const KIND_TO_TAG_COLOR: Readonly<
  Record<FreightRestrictionKind | 'unknown', string>
> = {
  hgv_no: 'red',
  access_no: 'red',
  motor_vehicle_no: 'red',
  hazmat_no: 'purple',
  maxweight: 'orange',
  maxheight: 'gold',
  maxwidth: 'volcano',
  maxlength: 'lime',
  maxaxleload: 'orange',
  other: 'default',
  unknown: 'default',
};

function _kindOrPlaceholder(
  state: LoadState,
): FreightRestrictionKind | 'unknown' {
  return state.status === 'success' ? state.record.primary_kind : 'unknown';
}

function _detailItems(record: FreightRestrictionRecord, t: TFunction) {
  const items: { key: string; label: string; children: string }[] = [];
  if (record.highway) {
    items.push({
      key: 'highway',
      label: t('freightRestrictions.fields.highway'),
      children: record.highway,
    });
  }
  if (record.hgv) {
    items.push({
      key: 'hgv',
      label: 'hgv',
      children: record.hgv,
    });
  }
  if (record.motor_vehicle) {
    items.push({
      key: 'motor_vehicle',
      label: 'motor_vehicle',
      children: record.motor_vehicle,
    });
  }
  if (record.access) {
    items.push({
      key: 'access',
      label: 'access',
      children: record.access,
    });
  }
  if (record.hazmat) {
    items.push({
      key: 'hazmat',
      label: 'hazmat',
      children: record.hazmat,
    });
  }
  if (record.maxweight !== null) {
    items.push({
      key: 'maxweight',
      label: t('freightRestrictions.fields.maxweight'),
      children: t('common.units.tonnes', { value: record.maxweight }),
    });
  }
  if (record.maxheight !== null) {
    items.push({
      key: 'maxheight',
      label: t('freightRestrictions.fields.maxheight'),
      children: t('common.units.metres', { value: record.maxheight }),
    });
  }
  if (record.maxwidth !== null) {
    items.push({
      key: 'maxwidth',
      label: t('freightRestrictions.fields.maxwidth'),
      children: t('common.units.metres', { value: record.maxwidth }),
    });
  }
  if (record.maxlength !== null) {
    items.push({
      key: 'maxlength',
      label: t('freightRestrictions.fields.maxlength'),
      children: t('common.units.metres', { value: record.maxlength }),
    });
  }
  if (record.maxaxleload !== null) {
    items.push({
      key: 'maxaxleload',
      label: t('freightRestrictions.fields.maxaxleload'),
      children: t('common.units.tonnes', { value: record.maxaxleload }),
    });
  }
  if (record.oneway) {
    items.push({
      key: 'oneway',
      label: 'oneway',
      children: record.oneway,
    });
  }
  return items;
}

function _containerStyle(token: GlobalToken): CSSProperties {
  return {
    position: 'absolute',
    top: 16,
    right: 16,
    width: POPUP_WIDTH_PX,
    background: 'rgba(255, 255, 255, 0.94)',
    backdropFilter: 'blur(12px) saturate(140%)',
    WebkitBackdropFilter: 'blur(12px) saturate(140%)',
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    boxShadow: token.boxShadow,
    zIndex: 410,
    pointerEvents: 'auto',
  };
}
